import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  CircularProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import LogoutIcon from '@mui/icons-material/Logout';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuthStore } from '../../stores/authStore';
import { clearDriveCache, setDriveAccountId } from '../../services/googleDriveService';
import { clearGalleryFolderCache } from '../../services/googleDriveImageService';
import { fetchGoogleUser } from '../../hooks/useDriveSession';
import {
  clearRememberedGoogleIdentity,
  claimUnassignedSyncEntities,
  setRememberedGoogleIdentity,
} from '../../services/localStorageService';
import { flushSyncOutbox } from '../../services/syncCoordinator';

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const FIVE_MINUTES_MS = 5 * 60 * 1000;

interface Props {
  /** When true, renders the signed-in state inline (e.g. in the drawer). */
  compact?: boolean;
  /** Shows a separate, always-visible logout action below the account control. */
  showLogoutButton?: boolean;
}

export const GoogleSignInButton: React.FC<Props> = ({
  compact = false,
  showLogoutButton = false,
}) => {
  const { user, accessToken, tokenExpiresAt, connectionStatus, syncStatus, lastSynced, setUser, setAccessToken, setConnectionStatus, setSyncStatus, signOut } =
    useAuthStore();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silentLoginRef = useRef<(() => void) | null>(null);

  const handleLoginSuccess = async (tokenResponse: { access_token: string; expires_in?: number }) => {
    const token = tokenResponse.access_token;
    const expiresAt = new Date(Date.now() + (tokenResponse.expires_in ?? 3540) * 1000);

    try {
      const googleUser = await fetchGoogleUser(token);
      setRememberedGoogleIdentity(googleUser);
      claimUnassignedSyncEntities(googleUser.sub);
      setDriveAccountId(googleUser.sub);
      setUser(googleUser);
    } catch (error) {
      setSyncStatus('error', error instanceof Error ? error.message : 'Google sign-in failed');
      setIsSigning(false);
      return;
    }

    setAccessToken(token, expiresAt);
    setConnectionStatus('connected');
    setSyncStatus('idle');
    setIsSigning(false);
  };

  const login = useGoogleLogin({
    onSuccess: (response) => {
      void handleLoginSuccess(response as { access_token: string; expires_in?: number });
    },
    onError: () => {
      setSyncStatus('error', 'Google sign-in failed');
      setIsSigning(false);
    },
    scope: DRIVE_SCOPE,
    flow: 'implicit',
  });

  const silentLogin = useGoogleLogin({
    onSuccess: async (response) => {
      const r = response as { access_token: string; expires_in?: number };
      try {
        const profile = await fetchGoogleUser(r.access_token);
        if (!user || profile.sub !== user.sub) {
          setAccessToken(null);
          setConnectionStatus('reauth-required');
          return;
        }
        const expiresAt = new Date(Date.now() + (r.expires_in ?? 3540) * 1000);
        setAccessToken(r.access_token, expiresAt);
        setConnectionStatus('connected');
      } catch {
        setAccessToken(null);
        setConnectionStatus('reauth-required');
      }
    },
    onError: () => {
      setAccessToken(null);
      setConnectionStatus('reauth-required');
    },
    scope: DRIVE_SCOPE,
    flow: 'implicit',
    prompt: '',
  });

  // Always keep the ref current so the timer effect never captures a stale closure.
  silentLoginRef.current = silentLogin;

  useEffect(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!tokenExpiresAt) {
      return;
    }

    const fire = () => silentLoginRef.current?.();
    const delay = tokenExpiresAt.getTime() - Date.now() - FIVE_MINUTES_MS;

    if (delay <= 0) {
      fire();
      return;
    }

    refreshTimerRef.current = setTimeout(fire, delay);

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [tokenExpiresAt]);

  const handleSignIn = () => {
    setIsSigning(true);
    login();
  };

  const handleSignOut = () => {
    clearDriveCache();
    clearGalleryFolderCache();
    setDriveAccountId(null);
    clearRememberedGoogleIdentity();
    signOut();
    setMenuAnchorEl(null);
  };

  const handleManualSync = async () => {
    if (!accessToken) return;
    setMenuAnchorEl(null);
    await flushSyncOutbox();
  };

  if (!user || !accessToken) {
    const isRestoring = connectionStatus === 'restoring';
    return (
      <Button
        variant="outlined"
        size={compact ? 'small' : 'medium'}
        startIcon={isSigning || isRestoring ? <CircularProgress size={16} /> : <GoogleIcon />}
        onClick={handleSignIn}
        disabled={isSigning || isRestoring}
        fullWidth
      >
        {isRestoring ? 'Restoring Google Drive' : user ? 'Reconnect Google Drive' : 'Sign in with Google'}
      </Button>
    );
  }

  const syncIcon = (() => {
    if (syncStatus === 'syncing') return <CircularProgress size={14} />;
    if (syncStatus === 'success') return <CheckCircleOutlineIcon fontSize="small" color="success" />;
    if (syncStatus === 'error') return <ErrorOutlineIcon fontSize="small" color="error" />;
    return null;
  })();

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: 'pointer', minWidth: 0 }}
        onClick={(e) => setMenuAnchorEl(e.currentTarget)}
      >
        <Tooltip title={user.email}>
          <Avatar
            src={user.picture}
            alt={user.name}
            sx={{ width: 28, height: 28 }}
          />
        </Tooltip>
        {!compact && (
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {user.name}
          </Typography>
        )}
        {syncIcon && (
          <Tooltip
            title={
              syncStatus === 'syncing'
                ? 'Syncing…'
                : syncStatus === 'success'
                ? `Last synced ${lastSynced?.toLocaleTimeString() ?? ''}`
                : useAuthStore.getState().syncError ?? 'Sync error'
            }
          >
            <span>{syncIcon}</span>
          </Tooltip>
        )}
      </Stack>

      {showLogoutButton && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LogoutIcon />}
          onClick={handleSignOut}
          fullWidth
          sx={{ mt: 1 }}
        >
          Logout
        </Button>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => void handleManualSync()} disabled={syncStatus === 'syncing'}>
          <ListItemIcon>
            <SyncIcon fontSize="small" />
          </ListItemIcon>
          Sync now
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};
